import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, Phone, Headphones, ClipboardList, Upload, FileText, Link2, Sparkles, CheckCircle2, AlertTriangle, ChevronRight, ChevronLeft, Loader2, ArrowRight, Lock } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useTenant } from '../TenantContext.jsx';
import { tenantsApi } from '../../api/tenants.js';
import { useBuildJobs, computeBuildProgress } from './BuildJobsContext.jsx';
import { RESPONSE_STYLES, voicePoolFor } from './voiceProviders.js';
import { normalizeLang } from '../../i18n';
import { overlayDismiss } from '../../lib/overlayDismiss';

const FEMALE_VOICE_POOL = ['Aoede', 'Kore', 'Leda', 'Zephyr', 'Achernar', 'Gacrux', 'Despina', 'Vindemiatrix', 'Pulcherrima', 'Autonoe', 'Callirrhoe'];

// 靜默確認秒數預設 —— 總機是「找人轉接」的短互動，等 20 秒才確認太久（晶吉 0731 反映）；
// 客服 / 問卷會有思考與翻資料的停頓，維持 runtime 全域預設 20。
const DEFAULT_REPROMPT_SEC = { 'transfer-only': 10, full: 20, survey: 20 };
function pickRandomVoice() {
    return FEMALE_VOICE_POOL[Math.floor(Math.random() * FEMALE_VOICE_POOL.length)];
}

/**
 * AddAvatarWizard — 新增 AI 角色 4-step wizard
 *
 * Steps:
 *   1. 類型(AI 總機 / AI 客服 / AI 問卷)
 *   2. 基本資訊(名稱 + 公司 + 開場白選填;survey 沒分機)
 *   3. 資料來源:
 *      - 'full'           → 上傳知識庫(產品手冊 / 網址)
 *      - 'survey'         → 上傳題庫(問卷 PDF / DOCX / TXT / 任意格式)
 *      - 'transfer-only'  → 確認頁(沒資料要傳)
 *   4. 建構進度(SSE stream)
 *
 * 音色:隨機從 11 個女聲挑,後續可在「音色及開場白」工作室換。
 * POST /ivr-builder/api/ivr/build 走 multipart/form-data + SSE response。
 */
export function AddAvatarWizard({ open, onClose, onBuilt }) {
    const { t, i18n } = useTranslation('ivrAgents');
    const { user } = useAuth();
    const { hostId } = useTenant();
    const navigate = useNavigate();
    const { jobs, startAvatarBuild, abort: abortJob } = useBuildJobs();
    const [step, setStep] = useState(1);

    // Step 1
    const [mode, setMode] = useState(null);   // 'transfer-only' | 'full' | 'survey'

    // Step 2
    const [name, setName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [firstMessage, setFirstMessage] = useState('');
    // 應答風格：'standard'(精準應答=gemini,預設) | 'instant'(即時互動=grok)。UI 不出現引擎名。
    const [responseStyle, setResponseStyle] = useState('standard');
    // 不預設 '0'：占位分機撥不通會讓轉真人直接斷線。留空 → 後端 transfer 節點 ext 留空 →
    // runtime 解析鏈自動走「tenant 預設分機 → 通訊錄 by-service」（2026-06-11 ext=0 事故修補）。
    const [transferExt, setTransferExt] = useState('');
    // 通話行為（晶吉 0731 反映）：以前只有 liveConfig 能設、UI 完全看不到 → 客戶不知道有這功能。
    //   interruptMode 'protectGreeting'(預設,開場白播完才收) | 'allow'(可隨時插話) | 'never'(全程不打斷)
    //   ⚠️ 預設是「開場白不可中斷」= 維持系統既有行為，避免既有客戶升級後行為突變。
    //   repromptSec   對話中靜默多久出聲確認「還在線上嗎」。AA(總機)是短互動 → 預設 10；客服維持 20。
    const [interruptMode, setInterruptMode] = useState('protectGreeting');
    const [repromptSec, setRepromptSec] = useState(DEFAULT_REPROMPT_SEC.full);
    // 「開場白播完，客戶完全沒出聲」要做什麼（問題三）：N 秒後轉總機 / 掛斷 / 不處理
    const [noResponseSec, setNoResponseSec] = useState(0);          // 0 = 不特別處理（走全域 60s 保護掛斷）
    const [noResponseAction, setNoResponseAction] = useState('transfer');
    const [noResponseExt, setNoResponseExt] = useState('');

    // Step 3 — 知識庫來源（三選一 tab，但實際可同時帶資料）
    const [kbSource, setKbSource] = useState('file');   // 'file' | 'text' | 'url'
    const [files, setFiles] = useState([]);   // File[]
    const [urls, setUrls] = useState('');     // 一行一個 URL — 一律整站深度爬取（BFS），介面不再提供單頁選項
    const [extractFocus, setExtractFocus] = useState('');   // 客製擷取重點（可選）— 同智慧擷取

    // Step 4 — 從 context 訂閱（modal 關了 build 仍跑）
    const [jobId, setJobId] = useState(null);
    const job = jobId ? jobs[jobId] : null;
    const events = job?.events || [];
    const running = job?.status === 'running';
    const completedAvatarId = job?.avatarId || null;
    const error = job?.error ? new Error(job.error) : null;

    useEffect(() => {
        if (!open) {
            // reset on close — 不 abort job（背景繼續）
            setStep(1);
            setMode(null);
            setName(''); setCompanyName(''); setFirstMessage(''); setTransferExt('0'); setResponseStyle('standard');
            setInterruptMode('protectGreeting'); setRepromptSec(DEFAULT_REPROMPT_SEC.full);
            setNoResponseSec(0); setNoResponseAction('transfer'); setNoResponseExt('');
            setKbSource('file'); setFiles([]); setUrls(''); setExtractFocus('');
            setJobId(null);
        }
    }, [open]);

    // 付費角色數（roleQuota）— 建到一半才被擋很浪費（KB 爬站要好幾分鐘），所以在第一步就先問後端。
    // 拿不到（舊帳號 / 超管 / 經銷商 / 讀失敗）一律當不限，不阻擋建立；真正的硬擋在 ivr-builder-api。
    const [roleUsage, setRoleUsage] = useState(null);
    useEffect(() => {
        if (!open || !hostId) { setRoleUsage(null); return; }
        let cancelled = false;
        tenantsApi.roleUsage(hostId)
            .then((r) => { if (!cancelled) setRoleUsage(r || null); })
            .catch(() => { if (!cancelled) setRoleUsage(null); });
        return () => { cancelled = true; };
    }, [open, hostId]);

    // 回傳擋下的原因文案，null = 沒超額。'transfer-only'=AA(plan_01)，其餘(客服/問卷)=CS(plan_02)。
    const quotaBlockFor = (m) => {
        if (!m || !roleUsage?.roleQuota) return null;
        const key = m === 'transfer-only' ? 'plan_01' : 'plan_02';
        const limit = roleUsage.roleQuota[key];
        if (typeof limit !== 'number') return null;
        const used = Number(roleUsage.used?.[key] || 0);
        if (used + 1 <= limit) return null;
        return t('wizard.roleLimitReached', {
            typeName: t(key === 'plan_01' ? 'wizard.roleTypeAA' : 'wizard.roleTypeCS'),
            used, limit,
        });
    };

    if (!open) return null;

    const quotaBlocked = quotaBlockFor(mode);

    const canNext = () => {
        if (step === 1) return !!mode && !!hostId && !quotaBlocked;   // 沒選 tenant / 角色數超額 不准往下
        if (step === 2) return name.trim().length > 0;
        if (step === 3) {
            // 知識庫可選填 — 沒給也可以建空 avatar 之後再加
            return true;
        }
        return false;
    };

    const handleFiles = (newFiles) => {
        const arr = Array.from(newFiles).filter(f => {
            if (f.size > 100 * 1024 * 1024) {
                alert(t('wizard.fileTooLarge', { name: f.name }));
                return false;
            }
            return true;
        });
        setFiles((prev) => [...prev, ...arr].slice(0, 10));
    };

    const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

    const handleBuild = async () => {
        const fd = new FormData();
        fd.append('ownerEmail', user?.email || '');
        fd.append('name', name.trim());
        fd.append('mode', mode);
        // hostId — 綁「建立當下所在的 tenant」（同一 owner 可屬多 tenant，如宏華 cti01/cti02，
        // 一律以 console 當下選的為準）。必帶：沒綁 tenant 的角色轉真人會解析不到分機直接斷線
        // （2026-06-11 掃出 58 隻中招）。canNext 已擋無 tenant，這裡再保險一次。
        if (!hostId) return;
        fd.append('hostId', hostId);
        // 應答風格 → 底層 provider + 音色。即時互動(grok) 只用於 IVR(full/transfer-only)，survey 不支援。
        // 🔴 AA(純轉接總機) 不支援即時互動(grok)：0805 東訊 23 通實測，grok 在總機情境
        //    會出現「客人講人名後完全不回應」「送話斷斷續續」「重複講轉接告知」。
        //    問卷同樣不支援。後端 build API 也有同一道保險。
        // 0914：改成查 RESPONSE_STYLES 表 —— 舊碼寫死送 'grok'（後端再轉 openai），而 builder 腳本只認 grok 才寫
        //   liveConfig.provider，所以「即時互動」其實一直跑在 Gemini 上（0911 掃出）。現在 instant→openai、live→openai-live。
        const styleProvider = RESPONSE_STYLES[responseStyle]?.provider || 'gemini';
        const useRealtime = styleProvider !== 'gemini' && mode !== 'survey' && mode !== 'transfer-only';
        if (useRealtime) {
            fd.append('provider', styleProvider);
            fd.append('voiceId', voicePoolFor(styleProvider).defaultVoice);
        } else {
            fd.append('voiceId', pickRandomVoice());
        }
        // 語言 = 操作者當下的 admin-console UI 語系（正規化成支援的 8 碼）。
        // 這個 language 同時決定「avatar 講的語言 / flow 生成語言(defaultLanguage)」＋「builder 對話語言」，
        // 所以日文 UI 建 avatar → 生成的 prompt / flow / 開場白全日文（不再寫死 zh-TW）。
        fd.append('language', normalizeLang(i18n.language));
        fd.append('liveMode', 'true');
        if (firstMessage.trim()) fd.append('firstMessage', firstMessage.trim());
        if (companyName.trim()) fd.append('companyName', companyName.trim());
        if (transferExt.trim()) fd.append('transferExt', transferExt.trim());
        // 通話行為 → liveConfig（builder skill 依這兩個 env 設 noInterruption/protectGreeting/repromptSec）。
        // survey 不吃這組（照稿唸題目，語意不適用）。
        if (mode !== 'survey') {
            fd.append('interruptMode', interruptMode);
            const sec = Number(repromptSec);
            if (Number.isFinite(sec) && sec >= 0) fd.append('repromptSec', String(Math.min(120, Math.round(sec))));
            const nrSec = Number(noResponseSec);
            if (Number.isFinite(nrSec) && nrSec > 0) {
                fd.append('noResponseSec', String(Math.min(120, Math.round(nrSec))));
                fd.append('noResponseAction', noResponseAction);
                if (noResponseAction === 'transfer' && noResponseExt.trim()) {
                    fd.append('noResponseExt', noResponseExt.trim());
                }
            }
        }
        if (mode === 'full') {
            files.forEach((f) => fd.append('files', f));
            // URLs → 一行一個,後端 build-ivr-avatar SKILL.md 會 WebFetch 每個 URL 抓內容
            const urlList = urls.split(/[\n,]+/).map((u) => u.trim()).filter((u) => /^https?:\/\//i.test(u));
            if (urlList.length > 0) {
                fd.append('urls', urlList.join('\n'));
                fd.append('deepCrawl', 'true');   // 一律整站深度爬取（介面不再提供單頁選項）
                if (extractFocus.trim()) fd.append('extractFocus', extractFocus.trim());
            }
        } else if (mode === 'survey') {
            // 題庫檔案 — 後端 build-survey-flow skill 解析成題目 + canvas flow
            files.forEach((f) => fd.append('files', f));
        }

        const newJobId = await startAvatarBuild({
            name: name.trim(),
            mode,
            hostId,
            formData: fd,
        });
        setJobId(newJobId);
        setStep(4);
    };

    // 「X」右上角 = 關閉視窗（建構中也允許，背景繼續）
    const handleClose = () => {
        if (completedAvatarId && onBuilt) onBuilt(completedAvatarId);
        onClose();
    };

    // 「取消建構」明確按鈕 = abort fetch + 關閉
    const handleAbort = () => {
        if (!confirm(t('wizard.abortConfirm'))) return;
        if (jobId) abortJob(jobId);
        onClose();
    };

    const handleGoWorkspace = () => {
        if (completedAvatarId) {
            if (onBuilt) onBuilt(completedAvatarId);
            navigate(`/ivr-agents/${completedAvatarId}`);
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 bg-ink-900/40 backdrop-blur-md z-50 grid place-items-center p-4"
            {...overlayDismiss(handleClose)}
        >
            <div className="bg-cream-50 border border-cream-200 rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-cream-200 flex items-center justify-between">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-400 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> new avatar
                        </div>
                        <h2 className="heading-display text-xl font-semibold text-ink-900 mt-0.5">{t('page.addAgent')}</h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="size-9 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Stepper */}
                <Stepper step={step} mode={mode} />

                {/* Step content */}
                <div className="px-6 py-5 flex-1 overflow-y-auto">
                    {step === 1 && (
                        <>
                            {!hostId && (
                                <div className="mb-3 px-3 py-2 rounded-md bg-danger-500/10 text-danger-500 text-sm">
                                    {t('wizard.tenantRequired')}
                                </div>
                            )}
                            {quotaBlocked && (
                                <div className="mb-3 px-3 py-2 rounded-md bg-warn-100/60 border border-warn-500/40 text-warn-500 text-sm flex items-start gap-2">
                                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                                    <span className="text-ink-700">{quotaBlocked}</span>
                                </div>
                            )}
                            <Step1Mode
                                mode={mode}
                                setMode={(m) => {
                                    setMode(m);
                                    setRepromptSec(DEFAULT_REPROMPT_SEC[m] ?? 20);
                                    // 插話預設跟 runtime 一致（lib/greetingProtection）：客服=開場白唸完再收；
                                    // 總機=可隨時插話（0812 東訊：報上名字就走，硬聽完開場白會吃掉前半句）。
                                    setInterruptMode(m === 'transfer-only' ? 'allow' : 'protectGreeting');
                                    // AA 不支援即時互動 → 切到 AA 時把殘留選擇拉回精準應答
                                    if (m === 'transfer-only') setResponseStyle('standard');
                                }}
                            />
                        </>
                    )}
                    {step === 2 && (
                        <Step2Basic
                            mode={mode}
                            name={name} setName={setName}
                            companyName={companyName} setCompanyName={setCompanyName}
                            firstMessage={firstMessage} setFirstMessage={setFirstMessage}
                            transferExt={transferExt} setTransferExt={setTransferExt}
                            responseStyle={responseStyle} setResponseStyle={setResponseStyle} hideResponseStyle={mode === 'transfer-only'}
                            interruptMode={interruptMode} setInterruptMode={setInterruptMode}
                            repromptSec={repromptSec} setRepromptSec={setRepromptSec}
                            noResponseSec={noResponseSec} setNoResponseSec={setNoResponseSec}
                            noResponseAction={noResponseAction} setNoResponseAction={setNoResponseAction}
                            noResponseExt={noResponseExt} setNoResponseExt={setNoResponseExt}
                        />
                    )}
                    {step === 3 && mode === 'full' && (
                        <Step3KB
                            kbSource={kbSource} setKbSource={setKbSource}
                            files={files} setFiles={handleFiles} removeFile={removeFile}
                            urls={urls} setUrls={setUrls}
                            extractFocus={extractFocus} setExtractFocus={setExtractFocus}
                        />
                    )}
                    {step === 3 && mode === 'survey' && (
                        <Step3Survey
                            files={files} setFiles={handleFiles} removeFile={removeFile}
                        />
                    )}
                    {step === 3 && mode === 'transfer-only' && (
                        <Step3Confirm name={name} mode={mode} companyName={companyName} transferExt={transferExt} firstMessage={firstMessage} interruptMode={interruptMode} repromptSec={repromptSec} />
                    )}
                    {step === 4 && (
                        <Step4Build
                            events={events}
                            running={running}
                            error={error}
                            completedAvatarId={completedAvatarId}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-cream-200 flex items-center justify-between">
                    {step > 1 && step < 4 && (
                        <button
                            onClick={() => setStep(step - 1)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                        >
                            <ChevronLeft size={14} />
                            {t('wizard.prev')}
                        </button>
                    )}
                    {step === 4 && running && (
                        <button
                            onClick={handleAbort}
                            className="text-xs text-ink-400 hover:text-red-600 transition underline underline-offset-2"
                        >
                            {t('wizard.abortBuild')}
                        </button>
                    )}
                    <div className="flex-1" />
                    {step < 3 && (
                        <button
                            onClick={() => setStep(step + 1)}
                            disabled={!canNext()}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-ink-900 text-cream-50 hover:bg-ink-700 disabled:bg-cream-300 disabled:cursor-not-allowed transition"
                        >
                            {t('wizard.next')}
                            <ChevronRight size={14} />
                        </button>
                    )}
                    {step === 3 && (
                        <button
                            onClick={handleBuild}
                            disabled={!canNext()}
                            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm rounded-md bg-accent-700 text-cream-50 hover:bg-accent-800 disabled:bg-cream-300 disabled:cursor-not-allowed transition font-medium"
                        >
                            <Sparkles size={14} />
                            {t('wizard.startBuild')}
                        </button>
                    )}
                    {step === 4 && running && (
                        <button
                            onClick={handleClose}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-ink-900 text-cream-50 hover:bg-ink-700 transition"
                            title={t('wizard.closeBgTitle')}
                        >
                            {t('common:actions.close')}
                        </button>
                    )}
                    {step === 4 && completedAvatarId && (
                        <button
                            onClick={handleGoWorkspace}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-accent-700 text-cream-50 hover:bg-accent-800 transition font-medium"
                        >
                            {t('wizard.enterWorkspace')}
                            <ArrowRight size={14} />
                        </button>
                    )}
                    {step === 4 && !running && !completedAvatarId && !error && (
                        <button
                            onClick={onClose}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                        >
                            {t('common:actions.close')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stepper({ step, mode }) {
    const { t } = useTranslation('ivrAgents');
    const step3Label =
        mode === 'transfer-only' ? t('stepper.confirm')
        : mode === 'survey' ? t('stepper.questionBank')
        : t('stepper.knowledgeBase');
    const labels = [
        { n: 1, label: t('stepper.type') },
        { n: 2, label: t('stepper.basicInfo') },
        { n: 3, label: step3Label },
        { n: 4, label: t('stepper.build') },
    ];
    return (
        <div className="px-6 py-3 bg-cream-100/40 border-b border-cream-200 flex items-center gap-2">
            {labels.map((s, i) => (
                <div key={s.n} className="flex items-center gap-2">
                    <div
                        className={[
                            'size-6 rounded-full inline-flex items-center justify-center font-mono text-[11px] font-medium tabular-nums transition',
                            step === s.n
                                ? 'bg-accent-700 text-cream-50'
                                : step > s.n
                                    ? 'bg-accent-300 text-ink-900'
                                    : 'bg-cream-200 text-ink-400',
                        ].join(' ')}
                    >
                        {step > s.n ? '✓' : s.n}
                    </div>
                    <span className={['text-xs', step === s.n ? 'text-ink-900 font-medium' : 'text-ink-400'].join(' ')}>
                        {s.label}
                    </span>
                    {i < labels.length - 1 && <div className="w-6 h-px bg-cream-300 mx-1" />}
                </div>
            ))}
        </div>
    );
}

function Step1Mode({ mode, setMode }) {
    const { t } = useTranslation('ivrAgents');
    // 只買 AA（總機）席次的帳號：客服(full)/問卷(survey) 為 CS 專屬 → 灰掉不可選，只能建總機
    const { hasCS } = useTenant();
    const csLocked = !hasCS;
    return (
        <div className="space-y-3">
            <p className="text-sm text-ink-500 mb-4">{t('step1.intro')}</p>
            <div className="grid grid-cols-3 gap-3">
                <ModeCard
                    selected={mode === 'transfer-only'}
                    onClick={() => setMode('transfer-only')}
                    icon={Phone}
                    title={t('flowMode.transferOnly')}
                    badge={t('step1.transfer.badge')}
                    description={t('step1.transfer.desc')}
                    hints={[t('step1.transfer.hint1'), t('step1.transfer.hint2'), t('step1.transfer.hint3')]}
                />
                <ModeCard
                    selected={mode === 'full'}
                    onClick={() => setMode('full')}
                    icon={Headphones}
                    title={t('flowMode.full')}
                    badge={t('step1.full.badge')}
                    description={t('step1.full.desc')}
                    hints={[t('step1.full.hint1'), t('step1.full.hint2'), t('step1.full.hint3')]}
                    disabled={csLocked}
                    lockedHint={t('step1.csLocked')}
                />
                <ModeCard
                    selected={mode === 'survey'}
                    onClick={() => setMode('survey')}
                    icon={ClipboardList}
                    title={t('flowMode.survey')}
                    badge={t('step1.survey.badge')}
                    description={t('step1.survey.desc')}
                    hints={[t('step1.survey.hint1'), t('step1.survey.hint2'), t('step1.survey.hint3')]}
                    disabled={csLocked}
                    lockedHint={t('step1.csLocked')}
                />
            </div>
        </div>
    );
}

function ModeCard({ selected, onClick, icon: Icon, title, badge, description, hints, disabled = false, lockedHint }) {
    return (
        <button
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            title={disabled ? lockedHint : undefined}
            aria-disabled={disabled}
            className={[
                'text-left p-5 rounded-lg border-2 transition group flex flex-col gap-3 relative',
                disabled
                    ? 'border-cream-200 bg-cream-50 opacity-50 cursor-not-allowed'
                    : selected
                        ? 'border-accent-500 bg-accent-50 shadow-[0_4px_20px_rgba(168,144,96,0.2)]'
                        : 'border-cream-200 bg-white hover:border-accent-300 hover:bg-cream-50',
            ].join(' ')}
        >
            {disabled && (
                <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] text-ink-400">
                    <Lock size={11} />
                </span>
            )}
            <div className="flex items-center gap-3">
                <div className={[
                    'inline-flex items-center justify-center size-10 rounded-md',
                    selected ? 'bg-accent-200' : 'bg-cream-100 group-hover:bg-accent-50',
                ].join(' ')}>
                    <Icon size={20} strokeWidth={1.7} className="text-accent-700" />
                </div>
                <div>
                    <div className="heading-display text-lg font-semibold text-ink-900">{title}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-accent-700">{badge}</div>
                </div>
            </div>
            <p className="text-sm text-ink-700 leading-relaxed">{description}</p>
            <ul className="text-[11px] text-ink-500 space-y-0.5 mt-1">
                {hints.map((h) => <li key={h}>{h}</li>)}
            </ul>
            {disabled && lockedHint && (
                <p className="text-[11px] text-accent-700 mt-1 font-medium">{lockedHint}</p>
            )}
        </button>
    );
}

function ResponseStyleSelector({ value, onChange }) {
    const { t } = useTranslation('ivrAgents');
    const opts = [
        {
            id: 'standard', title: t('responseStyle.standard.title'), badge: t('responseStyle.standard.badge'),
            desc: t('responseStyle.standard.desc'),
            hint: t('responseStyle.standard.hint'),
        },
        {
            id: 'live', title: t('responseStyle.live.title'), badge: t('responseStyle.live.badge'),
            desc: t('responseStyle.live.desc'),
            hint: t('responseStyle.live.hint'),
        },
    ];
    return (
        <div>
            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">{t('responseStyle.label')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {opts.map((o) => {
                    const sel = value === o.id;
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => onChange(o.id)}
                            className={[
                                'text-left p-3.5 rounded-lg border-2 transition flex flex-col gap-1.5',
                                sel ? 'border-accent-500 bg-accent-50 shadow-[0_4px_20px_rgba(168,144,96,0.18)]'
                                    : 'border-cream-200 bg-white hover:border-accent-300 hover:bg-cream-50',
                            ].join(' ')}
                        >
                            <div className="flex items-center gap-2">
                                <span className={['size-4 rounded-full border-2 grid place-items-center shrink-0',
                                    sel ? 'border-accent-500' : 'border-cream-300'].join(' ')}>
                                    {sel && <span className="size-2 rounded-full bg-accent-500" />}
                                </span>
                                <span className="heading-display text-base font-semibold text-ink-900">{o.title}</span>
                                {o.badge && <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent-700 text-cream-50">{o.badge}</span>}
                            </div>
                            <p className="text-[12.5px] text-ink-500 leading-relaxed">{o.desc}</p>
                            <p className="font-mono text-[10px] text-ink-400">{o.hint}</p>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * CallBehaviorSelector — 通話行為：能不能插話 + 客戶不出聲時等多久
 *
 * 這兩件事 runtime 一直支援（liveConfig.noInterruption / protectGreeting / repromptSec），
 * 但以前只有「跟 AI builder 對話」能設 → 客戶根本不知道有這功能（晶吉 0731 反映）。
 * 這裡把它變成建角色時就看得到的選項。0908 起也給既有角色的工作室（CallBehaviorModal）共用。
 *
 * defaultMode：哪個選項掛「預設」徽章 —— 客服 'protectGreeting'、總機 'allow'（跟 runtime 預設一致）。
 */
export function CallBehaviorSelector({ interruptMode, setInterruptMode, repromptSec, setRepromptSec, noResponseSec, setNoResponseSec, noResponseAction, setNoResponseAction, noResponseExt, setNoResponseExt, defaultMode = 'protectGreeting' }) {
    const { t } = useTranslation('ivrAgents');
    const badge = t('callBehavior.interrupt.protectGreeting.badge');
    const opts = [
        { id: 'protectGreeting', title: t('callBehavior.interrupt.protectGreeting.title'), badge: defaultMode === 'protectGreeting' ? badge : null, desc: t('callBehavior.interrupt.protectGreeting.desc') },
        { id: 'allow', title: t('callBehavior.interrupt.allow.title'), badge: defaultMode === 'allow' ? badge : null, desc: t('callBehavior.interrupt.allow.desc') },
        { id: 'never', title: t('callBehavior.interrupt.never.title'), badge: null, desc: t('callBehavior.interrupt.never.desc') },
    ];
    // 0 = 關閉主動確認（客戶不出聲就一直等，由 60 秒無回應保護掛斷兜底）
    const secNum = Number(repromptSec);
    const disabled = !Number.isFinite(secNum) || secNum <= 0;
    // 0 = 不特別處理「開場白後完全沒出聲」，仍走全域 60 秒保護掛斷
    const nrSecNum = Number(noResponseSec);
    const nrOff = !Number.isFinite(nrSecNum) || nrSecNum <= 0;
    return (
        <div className="space-y-3">
            <div>
                <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">
                    {t('callBehavior.interrupt.label')}
                </label>
                <div className="grid grid-cols-3 gap-2.5">
                    {opts.map((o) => {
                        const sel = interruptMode === o.id;
                        return (
                            <button
                                key={o.id}
                                type="button"
                                onClick={() => setInterruptMode(o.id)}
                                className={[
                                    'text-left p-3 rounded-lg border-2 transition flex flex-col gap-1.5',
                                    sel ? 'border-accent-500 bg-accent-50 shadow-[0_4px_20px_rgba(168,144,96,0.18)]'
                                        : 'border-cream-200 bg-white hover:border-accent-300 hover:bg-cream-50',
                                ].join(' ')}
                            >
                                <div className="flex items-center gap-1.5">
                                    <span className={['size-3.5 rounded-full border-2 grid place-items-center shrink-0',
                                        sel ? 'border-accent-500' : 'border-cream-300'].join(' ')}>
                                        {sel && <span className="size-1.5 rounded-full bg-accent-500" />}
                                    </span>
                                    <span className="heading-display text-[13.5px] font-semibold text-ink-900">{o.title}</span>
                                    {o.badge && <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent-700 text-cream-50">{o.badge}</span>}
                                </div>
                                <p className="text-[11.5px] text-ink-500 leading-relaxed">{o.desc}</p>
                            </button>
                        );
                    })}
                </div>
            </div>
            <div>
                <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">
                    {t('callBehavior.reprompt.label')}
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="number"
                        min={0}
                        max={120}
                        value={repromptSec}
                        onChange={(e) => setRepromptSec(e.target.value)}
                        className="w-20 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                    />
                    <span className="text-xs text-ink-500">{t('callBehavior.reprompt.unit')}</span>
                </div>
                <div className="text-[10.5px] text-ink-400 mt-1 leading-relaxed">
                    {disabled ? t('callBehavior.reprompt.hintOff') : t('callBehavior.reprompt.hint', { sec: secNum })}
                </div>
            </div>
            <div>
                <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">
                    {t('callBehavior.noResponse.label')}
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="number"
                        min={0}
                        max={120}
                        value={noResponseSec}
                        onChange={(e) => setNoResponseSec(e.target.value)}
                        className="w-20 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                    />
                    <span className="text-xs text-ink-500">{t('callBehavior.noResponse.unit')}</span>
                    <select
                        value={noResponseAction}
                        onChange={(e) => setNoResponseAction(e.target.value)}
                        disabled={nrOff}
                        className="px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white disabled:opacity-40"
                    >
                        <option value="transfer">{t('callBehavior.noResponse.actionTransfer')}</option>
                        <option value="hangup">{t('callBehavior.noResponse.actionHangup')}</option>
                    </select>
                    {/* w-40：英文 placeholder「extension (optional)」在 w-28 會被截成「extension (」 */}
                    {!nrOff && noResponseAction === 'transfer' && (
                        <input
                            value={noResponseExt}
                            onChange={(e) => setNoResponseExt(e.target.value)}
                            placeholder={t('callBehavior.noResponse.extPlaceholder')}
                            className="w-40 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                        />
                    )}
                </div>
                <div className="text-[10.5px] text-ink-400 mt-1 leading-relaxed">
                    {nrOff ? t('callBehavior.noResponse.hintOff') : t('callBehavior.noResponse.hint', { sec: nrSecNum })}
                </div>
            </div>
        </div>
    );
}

function Step2Basic({ mode, name, setName, companyName, setCompanyName, firstMessage, setFirstMessage, transferExt, setTransferExt, responseStyle, setResponseStyle, hideResponseStyle, interruptMode, setInterruptMode, repromptSec, setRepromptSec, noResponseSec, setNoResponseSec, noResponseAction, setNoResponseAction, noResponseExt, setNoResponseExt }) {
    const { t } = useTranslation('ivrAgents');
    return (
        <div className="space-y-4">
            <p className="text-sm text-ink-500">{t('step2.intro1')}<strong className="text-ink-700">{t('step2.introStrong')}</strong>{t('step2.intro2')}</p>

            {/* 應答風格（survey / AA 不支援即時互動 —— 見 hideResponseStyle 註解） */}
            {mode !== 'survey' && !hideResponseStyle && (
                <ResponseStyleSelector value={responseStyle} onChange={setResponseStyle} />
            )}
            <div>
                <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">{t('step2.nameLabel')}</label>
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('step2.namePlaceholder')}
                    autoFocus
                    className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                />
            </div>
            <div>
                <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">{t('step2.companyLabel')}</label>
                <input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t('step2.companyPlaceholder')}
                    className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                />
                <div className="text-[10.5px] text-ink-400 mt-1">{t('step2.companyHint')}</div>
            </div>
            <div>
                <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">
                    {t('step2.greetingLabel')}
                </label>
                <textarea
                    value={firstMessage}
                    onChange={(e) => setFirstMessage(e.target.value)}
                    rows={2}
                    placeholder={
                        mode === 'transfer-only' ? t('step2.greetingPlaceholderTransfer')
                        : mode === 'survey' ? t('step2.greetingPlaceholderSurvey')
                        : t('step2.greetingPlaceholderFull')
                    }
                    className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white leading-relaxed"
                />
            </div>
            {/* survey 沒分機;IVR(full / transfer-only)才顯示 */}
            {mode !== 'survey' && (
                <div>
                    <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-1.5">
                        {mode === 'transfer-only' ? t('step2.extLabelTransfer') : t('step2.extLabelFull')}
                    </label>
                    <input
                        value={transferExt}
                        onChange={(e) => setTransferExt(e.target.value)}
                        placeholder="0"
                        className="w-32 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono"
                    />
                </div>
            )}
            {/* 通話行為 — 問卷是照稿唸題目，不適用「隨時插話 / 靜默確認」這組語意 */}
            {mode !== 'survey' && (
                <div className="pt-1 border-t border-cream-200">
                    <div className="pt-3">
                        <CallBehaviorSelector
                            interruptMode={interruptMode} setInterruptMode={setInterruptMode}
                            repromptSec={repromptSec} setRepromptSec={setRepromptSec}
                            noResponseSec={noResponseSec} setNoResponseSec={setNoResponseSec}
                            noResponseAction={noResponseAction} setNoResponseAction={setNoResponseAction}
                            noResponseExt={noResponseExt} setNoResponseExt={setNoResponseExt}
                            defaultMode={mode === 'transfer-only' ? 'allow' : 'protectGreeting'}
                        />
                    </div>
                </div>
            )}
            <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 leading-relaxed">
                {t('step2.voiceTip')}
            </div>
        </div>
    );
}

function Step3KB({ kbSource, setKbSource, files, setFiles, removeFile, urls, setUrls, extractFocus, setExtractFocus }) {
    // wsApi namespace 借用智慧擷取（SmartExtractModal）現成的「客製擷取重點」翻譯，兩邊文案同步
    const { t } = useTranslation(['ivrAgents', 'wsApi']);
    // 收合式 — 預設收起只佔一行，小視窗下也看得到入口；已有內容則展開
    const [focusOpen, setFocusOpen] = useState(Boolean(extractFocus.trim()));
    const onDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files?.length) setFiles(e.dataTransfer.files);
    };
    const urlCount = urls.split(/[\n,]+/).map((u) => u.trim()).filter((u) => /^https?:\/\//i.test(u)).length;
    const tabs = [
        { id: 'file', label: t('step3kb.tabFile'), icon: Upload, badge: files.length || null },
        { id: 'url', label: t('step3kb.tabUrl'), icon: Link2, badge: urlCount || null },
    ];
    return (
        <div className="space-y-5">
            <p className="text-sm text-ink-500 leading-relaxed">
                {t('step3kb.intro')}
                <span className="block text-ink-400 mt-0.5 text-[13px]">{t('step3kb.introHint')}</span>
            </p>

            {/* Source tabs — 2 選 1 segmented control */}
            <div className="flex gap-1.5 p-1 bg-cream-100 rounded-lg">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = kbSource === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setKbSource(t.id)}
                            className={[
                                'flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm transition',
                                active ? 'bg-white shadow-sm text-ink-900 font-medium' : 'text-ink-500 hover:text-ink-700',
                            ].join(' ')}
                        >
                            <Icon size={15} />
                            {t.label}
                            {t.badge != null && (
                                <span className="text-[10px] font-mono bg-accent-100 text-accent-700 rounded-full min-w-[18px] text-center px-1 leading-[18px]">
                                    {t.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* File panel */}
            {kbSource === 'file' && (
                <div className="space-y-2">
                    <label
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={onDrop}
                        className="block w-full border-2 border-dashed border-cream-300 hover:border-accent-400 rounded-lg p-8 text-center cursor-pointer hover:bg-cream-50 transition"
                    >
                        <input
                            type="file"
                            multiple
                            accept=".pdf,.docx,.xlsx,.pptx,.md,.txt,.png,.jpg,.jpeg,.webp"
                            onChange={(e) => setFiles(e.target.files)}
                            className="hidden"
                        />
                        <Upload size={24} className="mx-auto text-ink-300 mb-2.5" />
                        <div className="text-sm text-ink-700 font-medium">{t('step3kb.dropFile')}</div>
                        <div className="text-[11px] text-ink-400 mt-1.5">{t('step3kb.dropFileHint')}</div>
                    </label>
                    {files.length > 0 && (
                        <ul className="space-y-1">
                            {files.map((f, i) => (
                                <li key={i} className="flex items-center gap-2 text-xs bg-white border border-cream-200 rounded-md px-3 py-2">
                                    <FileText size={14} className="text-accent-700 shrink-0" />
                                    <span className="flex-1 truncate text-ink-700">{f.name}</span>
                                    <span className="text-ink-400 font-mono tabular-nums shrink-0">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                                    <button type="button" onClick={() => removeFile(i)} className="text-ink-400 hover:text-red-600">
                                        <X size={13} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* URL panel */}
            {kbSource === 'url' && (
                <div className="space-y-3">
                    <textarea
                        value={urls}
                        onChange={(e) => setUrls(e.target.value)}
                        rows={4}
                        placeholder={t('step3kb.urlPlaceholder')}
                        className="w-full resize-none px-3.5 py-3 text-[13px] font-mono border border-cream-300 focus:border-accent-500 outline-none rounded-lg bg-white leading-relaxed"
                    />
                    <div className="text-[11px] text-ink-400 flex items-center justify-between">
                        <span>{t('step3kb.deepCrawlAlways')}</span>
                        {urlCount > 0 && <span className="font-mono tabular-nums text-accent-700">{t('step3kb.urlCount', { count: urlCount })}</span>}
                    </div>

                    {/* 客製擷取重點（可選）— 收合式，與智慧擷取（SmartExtractModal）同步 */}
                    <div className="rounded-lg border border-cream-300 bg-cream-50">
                        <button
                            type="button"
                            onClick={() => setFocusOpen((v) => !v)}
                            className="w-full flex items-center justify-between px-3 py-2.5 text-[13px] text-ink-800 hover:text-ink-900 transition"
                        >
                            <span className="font-medium">
                                {t('wsApi:extract.customLabel')}
                                {!focusOpen && extractFocus.trim() && <span className="ml-1.5 text-accent-700">✓</span>}
                            </span>
                            <ChevronRight size={14} className={['transition-transform', focusOpen ? 'rotate-90' : ''].join(' ')} />
                        </button>
                        {focusOpen && (
                            <div className="px-3 pb-3">
                                <textarea
                                    value={extractFocus}
                                    onChange={(e) => setExtractFocus(e.target.value)}
                                    rows={4}
                                    placeholder={t('wsApi:extract.customPlaceholder')}
                                    className="w-full resize-none px-3.5 py-3 text-[13px] border border-cream-300 focus:border-accent-500 outline-none rounded-lg bg-white leading-relaxed"
                                    autoFocus
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function Step3Survey({ files, setFiles, removeFile }) {
    const { t } = useTranslation('ivrAgents');
    const onDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files?.length) setFiles(e.dataTransfer.files);
    };
    return (
        <div className="space-y-5">
            <p className="text-sm text-ink-500 leading-relaxed">
                {t('step3survey.intro')}
                <span className="block text-ink-400 mt-0.5 text-[13px]">
                    {t('step3survey.introHint')}
                </span>
            </p>

            <label
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                className="block w-full border-2 border-dashed border-cream-300 hover:border-accent-400 rounded-lg p-8 text-center cursor-pointer hover:bg-cream-50 transition"
            >
                <input
                    type="file"
                    multiple
                    accept="*/*"
                    onChange={(e) => setFiles(e.target.files)}
                    className="hidden"
                />
                <ClipboardList size={24} className="mx-auto text-ink-300 mb-2.5" />
                <div className="text-sm text-ink-700 font-medium">{t('step3survey.drop')}</div>
                <div className="text-[11px] text-ink-400 mt-1.5">
                    {t('step3survey.dropHint')}
                </div>
            </label>

            {files.length > 0 && (
                <ul className="space-y-1">
                    {files.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs bg-white border border-cream-200 rounded-md px-3 py-2">
                            <FileText size={14} className="text-accent-700 shrink-0" />
                            <span className="flex-1 truncate text-ink-700">{f.name}</span>
                            <span className="text-ink-400 font-mono tabular-nums shrink-0">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                            <button type="button" onClick={() => removeFile(i)} className="text-ink-400 hover:text-red-600">
                                <X size={13} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div className="rounded-md bg-accent-50 border border-accent-300 px-3 py-2.5 text-[12px] text-accent-700 leading-relaxed">
                {t('step3survey.warn1')}<strong>{t('step3survey.warnStrong')}</strong>{t('step3survey.warn2')}
            </div>
        </div>
    );
}

function Step3Confirm({ name, mode, companyName, transferExt, firstMessage, interruptMode, repromptSec }) {
    const { t } = useTranslation('ivrAgents');
    return (
        <div className="space-y-4">
            <p className="text-sm text-ink-500">{t('step3confirm.intro')}</p>
            <div className="bg-white border border-cream-200 rounded-md divide-y divide-cream-100">
                <ConfirmRow label={t('step3confirm.type')} value={mode === 'transfer-only' ? t('step3confirm.typeTransfer') : t('step3confirm.typeFull')} />
                <ConfirmRow label={t('step3confirm.name')} value={name} />
                {companyName && <ConfirmRow label={t('step3confirm.company')} value={companyName} />}
                <ConfirmRow label={t('step3confirm.ext')} value={transferExt || '0'} />
                <ConfirmRow label={t('step3confirm.greeting')} value={firstMessage || t('step3confirm.greetingAuto')} mono={!firstMessage} />
                <ConfirmRow label={t('step3confirm.voice')} value={t('step3confirm.voiceValue')} mono />
                {mode !== 'survey' && (
                    <ConfirmRow
                        label={t('step3confirm.callBehavior')}
                        value={[
                            t(`callBehavior.interrupt.${interruptMode}.title`),
                            Number(repromptSec) > 0
                                ? t('step3confirm.repromptValue', { sec: Number(repromptSec) })
                                : t('step3confirm.repromptOff'),
                        ].join(' · ')}
                    />
                )}
            </div>
        </div>
    );
}

function ConfirmRow({ label, value, mono }) {
    return (
        <div className="px-3 py-2 flex items-start gap-3">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400 w-20 shrink-0 pt-0.5">{label}</span>
            <span className={['text-sm flex-1', mono ? 'text-ink-500 italic' : 'text-ink-900'].join(' ')}>{value}</span>
        </div>
    );
}

function Step4Build({ events, running, error, completedAvatarId }) {
    const { t } = useTranslation('ivrAgents');
    const { percent, stage, stageParams } = computeBuildProgress(events);
    return (
        <div className="space-y-3">
            {/* Status banner */}
            {running && (
                <div className="rounded-md bg-accent-50 border border-accent-200 px-4 py-3 flex items-start gap-3">
                    <Loader2 size={16} className="text-accent-700 animate-spin shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm text-ink-700 leading-relaxed">
                        <div className="flex items-baseline justify-between gap-3">
                            <span>{(stage ? t(stage, stageParams) : t('step4.buildingDefault'))}⋯</span>
                            <span className="font-mono text-xs text-accent-700 tabular-nums font-semibold">
                                {percent}%
                            </span>
                        </div>
                        <div className="mt-2 h-1.5 bg-accent-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-accent-500 transition-all duration-500 ease-out"
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <div className="text-[11px] text-ink-500 mt-1.5">
                            {t('step4.canClose')}
                        </div>
                    </div>
                </div>
            )}
            {!running && completedAvatarId && (
                <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 flex items-center gap-3">
                    <CheckCircle2 size={16} className="text-green-700 shrink-0" />
                    <div className="text-sm text-ink-900 font-medium">
                        {t('step4.builtSuccess')} avatarId: <span className="font-mono text-xs text-ink-600">{completedAvatarId}</span>
                    </div>
                </div>
            )}
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-3">
                    <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-ink-700 leading-relaxed">
                        <div className="font-medium text-ink-900">{t('event.buildFailed')}</div>
                        <div className="text-xs text-ink-500 mt-0.5 break-all">{error.message}</div>
                    </div>
                </div>
            )}

            {/* Event log */}
            <div className="bg-ink-900 text-cream-50 rounded-md p-3 font-mono text-[11px] leading-relaxed max-h-[320px] overflow-y-auto">
                {events.length === 0 ? (
                    <div className="text-cream-200/60 italic">{t('building.preparing')}</div>
                ) : (
                    events.map((ev, i) => (
                        <div key={i} className="py-0.5">
                            <span className="text-accent-300">[{ev.type || 'event'}]</span>{' '}
                            <span className="text-cream-100">
                                {ev.message || ev.text || ev.name || ev.toolName || JSON.stringify(ev).substring(0, 100)}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
